import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Wallet, CheckCircle, XCircle, Clock, Phone } from "lucide-react";
import { toast } from "sonner";
import BulkApproveButton from "./BulkApproveButton";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const formatDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const statusMap = {
  pending: { label: "بانتظار", color: "#F5A623", icon: Clock },
  approved: { label: "تم القبض", color: "#7ED321", icon: CheckCircle },
  rejected: { label: "مرفوض", color: "#E85D45", icon: XCircle },
};

const CashSubmissionsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [busy, setBusy] = useState(null);
  // Date-range filter (from-to). Empty = no range constraint.
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/cash-submissions`);
      setItems(data);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleAction = async (id, action) => {
    const msg = action === "approve"
      ? "تأكيد استلام المبلغ كاملاً من السائق؟ سيُصفَّر رصيده."
      : "رفض هذا التسليم؟";
    if (!(await confirmDialog(msg))) return;
    setBusy(id);
    try {
      await axios.patch(`${API}/admin/cash-submissions/${id}`, { action });
      toast.success(action === "approve" ? "تم استلام المبلغ" : "تم الرفض");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally { setBusy(null); }
  };

  // Apply status + date range filtering. Date range is INCLUSIVE on both
  // ends — start at 00:00 of `from`, end at 23:59 of `to`.
  const applyDateRange = (arr) => {
    if (!dateFrom && !dateTo) return arr;
    const fromTs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : -Infinity;
    const toTs = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : Infinity;
    return arr.filter((w) => {
      const t = w.created_at ? new Date(w.created_at).getTime() : 0;
      return t >= fromTs && t <= toTs;
    });
  };
  const filtered = applyDateRange(filter === "all" ? items : items.filter((w) => w.status === filter));
  const pending = items.filter((w) => w.status === "pending").length;
  const totalAmount = filtered.reduce((s, w) => s + Number(w.amount || 0), 0);

  // Preset quick-ranges — handy buttons next to the inputs.
  const pad = (n) => String(n).padStart(2, "0");
  const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const setRange = (days) => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - days);
    setDateFrom(isoDay(from));
    setDateTo(isoDay(to));
  };

  return (
    <div data-testid="cash-subs-tab" className="space-y-4">
      <h2 className="font-heading font-bold text-xl text-[#111814] flex items-center gap-2">
        <Wallet className="w-6 h-6 text-[#7ED321]" />
        تسليمات السائقين
        {pending > 0 && (
          <span className="bg-[#7ED321] text-white text-xs px-2.5 py-0.5 rounded-full font-bold animate-pulse">
            {pending} جديد
          </span>
        )}
      </h2>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2">
          {[
            { key: "pending", label: "بانتظار" },
            { key: "approved", label: "تم القبض" },
            { key: "rejected", label: "مرفوض" },
            { key: "all", label: "الكل" },
          ].map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              data-testid={`cs-filter-${f.key}`}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                filter === f.key ? "bg-[#2D4A3E] text-white" : "bg-white border border-[#E4EBE6] text-[#5C6E64]"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        <BulkApproveButton resource="cash-submissions" label="موافقة على الكل" count={pending} onDone={fetchData} />
      </div>

      {/* Date-range filter — admin can scope to today / last 3 days / last
          week, or pick a precise from-to window. The amount summary on
          the right shows the total of the filtered set. */}
      <div className="bg-white border border-[#E4EBE6] rounded-2xl p-3 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs font-bold text-[#5C6E64]">من:</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="cs-date-from"
            className="px-3 py-1.5 rounded-xl border-2 border-[#E4EBE6] text-sm"
          />
          <label className="text-xs font-bold text-[#5C6E64]">إلى:</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="cs-date-to"
            className="px-3 py-1.5 rounded-xl border-2 border-[#E4EBE6] text-sm"
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              data-testid="cs-clear-range"
              className="text-xs text-[#E85D45] hover:underline"
            >
              مسح
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-[#5C6E64]">روابط سريعة:</span>
          {[
            { label: "اليوم", days: 0 },
            { label: "قبل 3 أيام", days: 3 },
            { label: "قبل أسبوع", days: 7 },
            { label: "قبل شهر", days: 30 },
          ].map((p) => (
            <button
              key={p.label}
              onClick={() => setRange(p.days)}
              data-testid={`cs-quick-${p.days}`}
              className="text-xs bg-[#F0F9FF] hover:bg-[#E0F2FE] text-[#1E3A5F] font-bold px-2.5 py-1 rounded-full"
            >
              {p.label}
            </button>
          ))}
          {filtered.length > 0 && (
            <span className="text-xs font-bold text-[#15803D] ms-auto" data-testid="cs-total-summary">
              المجموع: <span dir="ltr">{fmt(totalAmount)} د.ع</span> · {filtered.length} سند
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-center text-[#5C6E64] py-8">جاري التحميل...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-[#E4EBE6]">
          <Wallet className="w-16 h-16 text-[#E4EBE6] mx-auto mb-3" />
          <p className="text-[#5C6E64]">لا توجد تسليمات</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((w, idx) => {
            const Icon = statusMap[w.status]?.icon || Clock;
            const color = statusMap[w.status]?.color || "#999";
            return (
              <motion.div key={w.id}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
                data-testid={`cs-${w.id}`}
                className="bg-white border border-[#E4EBE6] rounded-2xl p-4 space-y-3">
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <p className="font-bold text-[#111814]">{w.driver_name}</p>
                    <a href={`tel:${w.driver_phone}`} className="text-sm text-[#E85D45] flex items-center gap-1 mt-1 hover:underline">
                      <Phone className="w-3 h-3" />{w.driver_phone}
                    </a>
                    <p className="text-xs text-[#5C6E64] mt-1">{formatDate(w.created_at)}</p>
                  </div>
                  <span className="px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5"
                    style={{ backgroundColor: `${color}20`, color }}>
                    <Icon className="w-3 h-3" />
                    {statusMap[w.status]?.label}
                  </span>
                </div>

                <div className="bg-[#F3F6F4] rounded-xl p-3 text-center">
                  <p className="text-xs text-[#5C6E64]">المبلغ المُسلَّم</p>
                  <p className="font-bold text-2xl text-[#7ED321]">{fmt(w.amount)} د.ع</p>
                </div>

                {w.status === "pending" && (
                  <div className="flex gap-2">
                    <button onClick={() => handleAction(w.id, "approve")} disabled={busy === w.id}
                      data-testid={`cs-approve-${w.id}`}
                      className="flex-1 bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2.5 rounded-full text-sm disabled:opacity-50">
                      ✓ تأكيد استلام المبلغ
                    </button>
                    <button onClick={() => handleAction(w.id, "reject")} disabled={busy === w.id}
                      data-testid={`cs-reject-${w.id}`}
                      className="flex-1 bg-[#F3F6F4] hover:bg-[#E4EBE6] text-[#5C6E64] font-bold py-2.5 rounded-full text-sm disabled:opacity-50">
                      ✕ رفض
                    </button>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CashSubmissionsTab;
